import { Check, Copy } from "lucide-react";
import { MouseEvent, useEffect, useState } from "react";

import { Button } from "components/ui/button";
import { cn } from "utils/cn";

const COPIED_MS = 2000;

interface CopyButtonProps {
  value: string;
  // Read by screen readers, e.g. "Copy case reference".
  label?: string;
  className?: string;
}

function CopyButton({ value, label = "Copy", className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timeout);
  }, [copied]);

  const copy = (event: MouseEvent<HTMLButtonElement>) => {
    // Don't follow a link the button sits in, e.g. a clickable table row.
    event.preventDefault();
    navigator.clipboard.writeText(value).then(() => setCopied(true));
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={copy}
      aria-label={copied ? "Copied" : label}
      title={copied ? "Copied" : label}
      className={cn("h-8 w-8 text-muted-foreground", className)}
    >
      {copied ? <Check className="text-success" /> : <Copy />}
    </Button>
  );
}

export { CopyButton };
